<script>
    import {PATH_PREFIX} from '../../apiUrls.js';

    import {onMount} from 'svelte';
    import DataTable from '../DataGrid/DataTable.svelte';
    import ConfirmationModal from '../ConfirmationModal.svelte';
    import TextColumn from '../DataGrid/ColumnRenderers/TextColumn.svelte';
    import NumberColumn from '../DataGrid/ColumnRenderers/NumberColumn.svelte';
    import SelectColumn from '../DataGrid/ColumnRenderers/SelectColumn.svelte';
    import QuestionSelectionOptionsColumn from '../DataGrid/ColumnRenderers/QuestionSelectionOptionsColumn.svelte';
    import QuestionTextColumn from '../DataGrid/ColumnRenderers/QuestionTextColumn.svelte';
    import {submit, get} from '../../utils.js';
    import {validateForm} from '../Form/utils.js';
    import {sgettext} from '../../i18n.js';
    import yup from 'yup';
    import {QuestionType} from './constants.js';
    import Button from '../Button.svelte';
    import Errors from '../Errors.svelte';

    export let rows = [];
    export let elementVersionId = 0;
    export let damageGroupOptions = null;
    export let displayAIESOptions = false;
    $: collapseId = `quality-questions-editor-${elementVersionId}`;

    let actionUrl = `${PATH_PREFIX}/sw/api/elements_database/questions/`;
    let predefinedSelectionOptionApi = `${PATH_PREFIX}/sw/api/elements_database/questions/predefinedselectionquestions/available/`;
    let inspectionRangeOptionApi = `${PATH_PREFIX}/sw/api/elements_database/questions/inspectionranges/available/`;
    let inspectionSelectionOptionApi = `${PATH_PREFIX}/sw/api/elements_database/questions/inspectionselections/available/`;

    let reorderApiUrl = `${PATH_PREFIX}/sw/api/elements_database/element_version/questions/reorder/${elementVersionId}/`;

    const questionTypes = [
        {id: QuestionType.text, name: sgettext('$$TEXT$$')},
        {id: QuestionType.range, name: sgettext('$$RANGE$$')},
        {id: QuestionType.number, name: sgettext('$$NUMBER$$')},
        {id: QuestionType.photo, name: sgettext('$$PHOTO$$')},
        {id: QuestionType.signature, name: sgettext('$$SIGNATURE$$')},
        {id: QuestionType.confirmation, name: sgettext('$$CONFIRMATION$$')},
        {id: QuestionType.selection, name: sgettext('$$SELECTION$$')},
        {id: QuestionType.predefined, name: sgettext('$$PREDEFINED_SELECTION_1$$')},
        {id: QuestionType.timed, name: sgettext('$$TIMED$$')},
    ];
    if(displayAIESOptions==true){
        questionTypes.push(
            {id: QuestionType.inspection_selection, name: sgettext('$$INSPECTION_SELECTION$$')},
            {id: QuestionType.inspection_range, name: sgettext('$$INSPECTION_RANGE$$')}
        )
    }

    let selectedItem = null;
    let deleting = false;
    let deleteConfirmationId = 'deleteQuestionConfirmation';
    let predefinedSelectionOptions = [];
    let inspectionSelectionOptions = [];
    let inspectionRangeOptions = [];
    let saving = false;
    let errors = '';
    let bgColor = '#EDF1F2';
    let showCreateNew = true;

    // we need to regnerate the schema every time a row changes as one of the fields depends on the content of the rows
    function makeSchema(){
        return yup.object().shape({
            question_text: yup.string()
                .when('question_type', {
                    is: QuestionType.timed,
                    then: yup.string().nullable(),
                    otherwise: yup.string()
                        .required(sgettext('$$REQUIRED_FIELD$$'))
                        .max(950, sgettext("$$CANNOT_EXCEED_950_CHARACTERS$$")),
                }),
            question_type: yup.string()
                .required(sgettext('$$REQUIRED_FIELD$$')),
            range_from: yup.number()
                .nullable()
                .when('question_type', {
                    is: QuestionType.range,
                    then: yup.number()
                        .required(sgettext('$$REQUIRED_FIELD$$'))
                        .min(-100000, sgettext('$$VALUE_MUST_GREATER_EQUAL_100000$$'))
                }),
            range_to: yup.number()
                .nullable()
                .when('question_type', {
                    is: QuestionType.range,
                    then: yup.number()
                        .required(sgettext('$$REQUIRED_FIELD$$'))
                        .moreThan(yup.ref('range_from'), sgettext('$$VALUE_MUST_BE_GREATER_THAN_FROM$$'))
                        .max(100000, sgettext('$$VALUE_MUST_LESS_EQUAL_100000$$'))
                }),
            selection_text: yup.string()
                .nullable()

                .when('question_type', {
                    is: QuestionType.selection,
                    then: yup.string().required(sgettext('$$REQUIRED_FIELD$$'))
                }),
            predefined_selection_question: yup.string()
                .nullable()
                .when('question_type', {
                    is: QuestionType.predefined,
                    then: yup.string()
                        .required(sgettext('$$REQUIRED_FIELD$$'))
                        .typeError(sgettext('$$REQUIRED_FIELD$$'))
                }),
            inspection_selection: yup.string()
            .nullable()
            .when('question_type', {
                is: QuestionType.inspection_selection,
                then: yup.string()
                    .required(sgettext('$$REQUIRED_FIELD$$'))
                    .typeError(sgettext('$$REQUIRED_FIELD$$'))
            }),
            inspection_range: yup.string()
            .nullable()
            .when('question_type', {
                is: QuestionType.inspection_range,
                then: yup.string()
                    .required(sgettext('$$REQUIRED_FIELD$$'))
                    .typeError(sgettext('$$REQUIRED_FIELD$$'))
            }),
            duration: yup.number()
                .nullable()
                .when('question_type', {
                    is: QuestionType.timed,
                    then: yup.number()
                        .required(sgettext('$$REQUIRED_FIELD$$'))
                        .typeError(sgettext('$$REQUIRED_FIELD$$'))
                        .moreThan(0, sgettext('$$DURATION_MUST_GREATER_0$$'))
                        .max(3600, sgettext('$$DURATION_MUST_LESS_EQUAL_3600_1$$'))
                }),
            damage_group_code: yup.number().nullable(),
            code: yup.string().nullable().when('question_type', {
                is: QuestionType.inspection_range,
                then: yup.string().notOneOf(rows.filter(function(q){return q.question_type==QuestionType.inspection_range || q.question_type==QuestionType.inspection_selection}).map(q => q.code), sgettext('$$CODE_ALREADY_USED$$'))
                    .required(sgettext('$$REQUIRED_FIELD$$'))
                    .typeError(sgettext('$$REQUIRED_FIELD$$'))
                }).when('question_type', {
                    is: QuestionType.inspection_selection,
                    then: yup.string().notOneOf(rows.filter(function(q){return q.question_type==QuestionType.inspection_range || q.question_type==QuestionType.inspection_selection}).map(q => q.code), sgettext('$$CODE_ALREADY_USED$$'))
                        .required(sgettext('$$REQUIRED_FIELD$$'))
                        .typeError(sgettext('$$REQUIRED_FIELD$$'))
                }),
            data_type: yup.string().nullable(),
            uom: yup.string().nullable(),
            optional: yup.boolean().nullable(),

        });
    }
    let schema = makeSchema();

    $: data = rows.map(r => ({...r, fieldValidations: initialFieldValidations()}));

    $: showCreateNew = rows.find(r => !r.id) ? false : true;

    let columns = {
        question_type: {
            label: sgettext('$$TYPE$$'),
            renderer: SelectColumn,
            props: {
                onChange: handleRowChange,
                options: questionTypes,
                searchable: true
            }
        },
        question_text: {
            label: sgettext('$$QUESTION$$'),
            renderer: QuestionTextColumn,
            props: {
                onChange: handleRowChange,
                multiline: true
            }
        }
    }

    $: aggregatedColumns = {
        selectionOptions: {
            label: sgettext('$$SELECTION_OPTIONS$$'),
            renderer: QuestionSelectionOptionsColumn,
            props: {
                onChange: handleRowChange,
                predefinedSelectionOptions,
                inspectionRangeOptions,
                inspectionSelectionOptions,
                damageGroupOptions: damageGroupOptions.map(function(damage_group){
                    return{
                        id:damage_group.id,
                        name:damage_group.code,
                    }
                })
            }
        }
    }

    let rowActions = {
        remove: {
            iconClass: 'fas fa5-trash-alt',
            handler: (event, item) => {
                selectedItem = item;
                toggleModal('show');
            }
        }
    }

    let newRowActions = {
        add: {
            key: 'save',
            label: sgettext('$$ADD_1$$'),
            width: '100px',
            btnClass: 'outline-primary',
            handler: async (event, item) => {
                await saveItem(item);
            }
        },
        cancel: {
            label: sgettext('$$CANCEL$$'),
            width: '100px',
            btnClass: 'outline-secondary',
            handler: (event, item) => {
                selectedItem = item;
                showCreateNew = true;
                handleRemove();
            }
        }
    }

    async function getPredefinedSelectionOptions() {
        const resp = await get(predefinedSelectionOptionApi);
        if (resp.ok) {
            const json = await resp.json();
            predefinedSelectionOptions = json.results.map(o => {
                return {
                    id: o.id,
                    name: o.name,
                    description: o.predefined_selections.map(o => o.selection_name).join(", ")
                }
            });
        }
    }

    async function getInspectionRangeOptions() {
        const resp = await get(inspectionRangeOptionApi);

        if (resp.ok) {
            const json = await resp.json();
            inspectionRangeOptions = json.results.map(o => {
                return {
                    id: o.id,
                    name: o.name,
                    description: o.predefined_selections.map(o => o.selection_name).join(", ")
                }
            });
        }
    }

    async function getInspectionSelectionOptions() {
        const resp = await get(inspectionSelectionOptionApi);

        if (resp.ok) {
            const json = await resp.json();
            inspectionSelectionOptions = json.results.map(o => {
                return {
                    id: o.id,
                    name: o.name,
                    description: o.predefined_selections.map(o => o.selection_name).join(", ")
                }
            });
        }
    }

    function initialFieldValidations() {
        return {
            question_type: {
                isValid: true,
                errors: ''
            },
            question_text: {
                isValid: true,
                errors: ''
            },
            range_from: {
                isValid: true,
                errors: ''
            },
            range_to: {
                isValid: true,
                errors: ''
            },
            selection_text: {
                isValid: true,
                errors: ''
            },
            predefined_selection_question: {
                isValid: true,
                errors: ''
            },
            duration: {
                isValid: true,
                errors: ''
            },
            damage_group_code: {
                isValid: true,
                errors: ''
            },
            code: {
                isValid: true,
                errors: ''
            },
            data_type:{
                isValid: true,
                errors:''
            },
            uom:{
                isValid: true,
                errors:''
            },
            optional:{
                isValid: true,
                errors:''
            },
            inspection_selection:{
                isValid: true,
                errors:''
            },
            inspection_range:{
                isValid: true,
                errors:''
            },
        };
    }

    function handleRowChange(event, row) {
        errors = '';

        //prevent multiple timed question
        if (row.question_type === QuestionType.timed) {
            const existingTimed = data.filter(d => d.question_type === QuestionType.timed && d.sequence !== row.sequence);
            const filtered = data.filter(r => r.id);

            if (existingTimed.length) {
                //show error message
                errors = sgettext('$$ONE_TIMED_QUESTION_ALLOWED$$');
                data = [...filtered];
                showCreateNew = true;
            }
        }

        if (row.id && !errors) {
            saveItem(row);
        }
    }


    function handleCreateNew() {
        data = [...data, createNewItem()];
        showCreateNew = false;
    }

    function createNewItem() {
        return {
            sequence: getNextSequence(),
            question_type: '',
            question_text: '',
            range_from: 0,
            range_to: 0,
            selection_text: '',
            duration: null,
            predefined_selection_question: null,
            inspection_range: null,
            inspection_selection: null,
            fieldValidations: initialFieldValidations(),
            rowActions: newRowActions,
            damage_group_code: null,
            code: "",
            data_type:"character",
            uom:"",
            optional:false,
        }
    }

    async function handleRemove() {
        if (selectedItem) {
            deleting = true;

            if (selectedItem.id) {
                const url = `${actionUrl}${selectedItem.id}/`;
                const resp = await submit(url, 'DELETE');

                if (resp.ok) {
                    rows = data.filter(r => r.id !== selectedItem.id);
                    toggleModal('hide');
                    selectedItem = null;
                    schema=makeSchema();
                }
            } else {
                data = data.filter(r => r.id);
                toggleModal('hide');
                rows = [...data];
            }


            deleting = false;
        }
    }

    function cancelDelete() {
        toggleModal('hide');
    }

    function toggleModal(state) {
        jQuery(`#${deleteConfirmationId}`).modal(state);
    }

    function getNextSequence() {
        let next = 1;

        if (data.length === 1) {
            next = data[0].sequence + 1;
        } else if (data.length > 1) {
            const sorted = data.slice().sort((a, b) => b.sequence - a.sequence);
            if (sorted.length > 1) {
                next = sorted[0].sequence + 1;
            }
        }

        return next;
    }

    async function handleRowReordered(event) {
        const payload = event.detail.rows.filter(r => r.id).map(r => ({id: r.id, sequence: r.sequence}));
        const resp = await submit(reorderApiUrl, 'PATCH', {items: payload});

        if (resp.ok) {
            rows = await resp.json();
        }
    }

    async function saveItem(payload) {
        let resp = {};
        payload.element_version = elementVersionId;
        if (!payload.predefined_selection_question) {
            payload.predefined_selection_question = null;
        }
        if (!payload.inspection_range) {
            payload.inspection_range = null;
        }
        if (!payload.inspection_selection) {
            payload.inspection_selection = null;
        }
        try {
            await validateForm(schema, payload);
            saving = true;
            if (payload.id) {
                const url = `${actionUrl}${payload.id}/`;
                resp = await submit(url, 'PATCH', payload);
            } else {
                resp = await submit(actionUrl, 'POST', payload);
            }

            if (resp.ok) {
                const json = await resp.json();
                let items = data.filter(t => t.id);
                json.fieldValidations = initialFieldValidations();
                if (!payload.id) {
                    data = [...items, json];
                } else {
                    data = [...items];
                }

                if (!payload.id) {
                    handleCreateNew();
                }

                rows = [...data];
                schema = makeSchema();
            } else {
                //TODO: show error message
            }
        } catch (err) {
            if (err.inner) {
                err.inner.forEach((er) => {
                    payload.fieldValidations[er.path].isValid = false;
                    payload.fieldValidations[er.path].errors = er.message;
                });
                data = [...data];
            }
        } finally {
            saving = false;
            const newRow = data.find(r => !r.id);
            if (!newRow) {
                showCreateNew = true;
            }
        }

    }

    onMount(async () => {
        await getPredefinedSelectionOptions();
        await getInspectionRangeOptions();
        await getInspectionSelectionOptions();
    });
</script>

<style>
    .wrapper {
        margin-bottom: 15px;
    }

    .actions {
        margin-top: 15px;
        display: flex;
        justify-content: flex-end;
    }
</style>

<div class="wrapper">
    <DataTable
        {columns}
        {aggregatedColumns}
        rows={data}
        {rowActions}
        {schema}
        dragMode="bodyRow"
        sortFunction={(a, b) => a.sequence - b.sequence}
        on:reordered={handleRowReordered}
        {bgColor}
        {saving}
    />

    <Errors errors={errors}/>

    {#if showCreateNew}
        <div class="actions d-flex flex-nowrap justify-content-end">
            <button class="btn btn-outline-primary" role="button" on:click={handleCreateNew}>
                {sgettext('$$CREATE_NEW_1$$')}
            </button>
        </div>
    {/if}
</div>

<ConfirmationModal
    id={deleteConfirmationId}
    title="{sgettext('$$DELETE_CONFIRMATION$$')}"
    onYes={handleRemove}
    onNo={cancelDelete}
    sending={deleting}>
    <div slot="message">
        <p>{sgettext('$$SURE_WANT_DELETE_QUESTION$$')}</p>
        {#if selectedItem}
            {#if selectedItem.question_type}
                <div>{selectedItem.question_type}</div>
            {/if}
            {#if selectedItem.question_text}
                <div>{selectedItem.question_text}</div>
            {/if}
        {/if}
        <br/>
    </div>

</ConfirmationModal>
